import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
  Typography,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  FormHelperText,
  IconButton,
  Tooltip,
  Box
} from '@mui/material';
import {mdiInformationOutline} from '@mdi/js'
import Icon from '@mdi/react';
import {CutCopyPasteTextField} from '../CommonComponents/CustomInputs';
import _ from "lodash"
import { useTranslation } from "react-i18next";
import AddSupportingFiles from "./AddSupportingFiles";

const PREFIX = 'ResidualDisclosure';

const classes = {
  emphasisBox: `${PREFIX}-emphasisBox`,
  indentedSection: `${PREFIX}-indentedSection`,
  textFieldInfoIcon: `${PREFIX}-textFieldInfoIcon`,
  legendInfoIcon: `${PREFIX}-legendInfoIcon`,
  addBtn: `${PREFIX}-addBtn`,
  mb3: `${PREFIX}-mb3`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.emphasisBox}`]: {
    background: theme.palette.grey[100],
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderLeftStyle: 'solid',
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.primary.main,
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },

  [`& .${classes.indentedSection}`]: {
    display: 'inline-block',
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(3),
    borderLeft: '1px solid',
    borderLeftColor: theme.palette.divider,
  },

  [`& .${classes.textFieldInfoIcon}`]: {
    paddingLeft: theme.spacing(1),
  },

  [`& .${classes.legendInfoIcon}`]: {
    marginTop: theme.spacing(-1.25),
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.addBtn}`]: {
    'marginTop': theme.spacing(-1),
    'borderStyle': 'dashed',
    'width': '100%',
    'borderColor': alpha(theme.palette.common.black, 0.23),
    '&.MuiButton-outlinedPrimary:hover': {
      borderStyle: 'dashed',
    },
  },

  [`& .${classes.mb3}`]: {
    marginBottom: theme.spacing(3),
  }
}));

export default function ResidualDisclosure(props) {

  const {t} = useTranslation();
  const yesAnswer = t("requestForm.yes");
  const noAnswer = t("requestForm.no");

  const handleValueChange=(e)=>{
    props.setFormData({
      ...props.formData,
      [e.target.name]: e.target.value
    });

    if(Boolean(props.errors.residualDisclosure?.[e.target.name]) && e.target.value!==""){
      let errs = _.cloneDeep(props.errors);
      delete errs.residualDisclosure[e.target.name];
      props.setErrors(errs);
    }
  };

  const handleConditionalValueChange=(e)=>{
    let newValues={};
    let newSupportingFiles = {...props.formData.supportingFiles};
    switch(e.target.name){
      case "subsetVariables":
        if(e.target.value !== "Yes"){
          newValues = {
            subsetVariables: e.target.value,
            subsetVariables_explainChanges: "",
          };
          //subsetVariables_changedSubSample: "",
          //subsetVariables_droppedOutliers: "",
          //subsetVariables_imputedData: "",
          //subsetVariables_modifiedVariables: "",
          //newSupportingFiles.subsetVariables_residualTablesFile=[];
        }else{
          newValues = {
            subsetVariables: e.target.value,
          };
        }
        break;
      case "previousRelease":
        if(e.target.value !== "Yes"){
          newValues = {
            previousRelease: e.target.value,
            previousRelease_changedSubSample: "",
            previousRelease_droppedOutliers: "",
            previousRelease_imputedData: "",
            previousRelease_modifiedVariables: "",
            previousRelease_explainChanges: "",
            previousRelease_dateRelation: "",
          };
          newSupportingFiles.previousRelease_residualTablesFile=[];
          newSupportingFiles.previousRelease_bothSetsFile=[];
        }else{
          newValues = {
            previousRelease: e.target.value,
          };
        }
        break;
    }

    props.setFormData({
      ...props.formData,
      ...newValues,
      supportingFiles: newSupportingFiles
    });

    if(props.errors.residualDisclosure){
      let errs=_.cloneDeep(props.errors);
      let errFlag=false;
      Object.keys(props.errors.residualDisclosure).forEach(k=>{
        if(k.startsWith(e.target.name)){
          delete errs.residualDisclosure[k];
          errFlag=true;
        }
        if(k.startsWith(`supportingFiles.${e.target.name}.`)){
          delete errs.residualDisclosure[k];
          errFlag=true;
        }
      });
      if(errFlag){
        props.setErrors(errs);
      }
    }
  };

  const supportingFilesSection=(fileType,mandatorySection=true,extraParagraph=false)=>{
    let fileTypeDisplay=null;
    if(fileType.includes("residualTablesFile")){
      fileTypeDisplay="residualTablesFile";
    }
    if(fileType.includes("bothSetsFile")){
      fileTypeDisplay="bothSetsFile";
    }

    return <AddSupportingFiles 
      step="residualDisclosure"
      filesList={props.filesList}
      supportingFiles={props.formData.supportingFiles}
      fileType={fileType}
      fileTypeDisplay={fileTypeDisplay}
      readOnly={props.readOnly || props.analystEditFlag}
      errors={props.errors}
      setErrors={props.setErrors}
      handleUpdateSupportingFiles={(newVal)=>{
        props.setFormData({
          ...props.formData,
          supportingFiles: newVal
        });
      }}
      addBtnClass={`${classes.addBtn} ${classes.mb3}`}
      mandatorySection={mandatorySection}
      extraParagraph={extraParagraph}
    />
  }

  return (
    <Root>
      <Typography component="h2" variant="h6" sx={{mb: 3}}>
        {t("requestForm.residualDisclosure.title")}
      </Typography>
      <Typography sx={{mb: 3}}>
        {t("requestForm.residualDisclosure.description")}
      </Typography>
      <Divider sx={{mb: 3}} />
      <Grid container>
        <Grid xs item>
          <FormControl
            component="fieldset"
            sx={{mb: 2}}
            error={Boolean(props.errors.residualDisclosure?.subsetVariables)}
            required
            aria-describedby="subsetVariables-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.residualDisclosure.subsetVariables")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="subsetVariables-helperText">
              {t(props.errors.residualDisclosure?.subsetVariables)}
            </FormHelperText>
            <RadioGroup
              id="subsetVariables"
              name="subsetVariables"
              onChange={props.readOnly ? null : handleConditionalValueChange}
              value={props.formData.subsetVariables}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid component="span" className={classes.legendInfoIcon} item>
          <Tooltip title={t("requestForm.residualDisclosure.subsetVariablesTip")} arrow describeChild>
            <IconButton
              aria-label={t('requestForm.toolTipMoreInformation')}
              edge="end"
              size="large">
              <Icon path={mdiInformationOutline} size={1} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      {props.formData.subsetVariables==="Yes" && 
        <Box className={classes.indentedSection} sx={{mb: 3}}>
          <Box sx={{mb: 3}}>
            <CutCopyPasteTextField
              id="subsetVariables_explainChanges"
              name="subsetVariables_explainChanges"            
              margin="dense"
              label={t("requestForm.residualDisclosure.listSubsetted")}
              multiline
              rows="3"
              variant="outlined"
              fullWidth
              required
              onBlur={handleValueChange}
              defaultValue={props.formData.subsetVariables_explainChanges}
              error={Boolean(props.errors.residualDisclosure?.subsetVariables_explainChanges)}
              helperText={t(props.errors.residualDisclosure?.subsetVariables_explainChanges)}
              inputProps={{readOnly: props.readOnly}}
            />
          </Box>
        </Box>
      }
      <FormControl
        component="fieldset"
        sx={{mb: 2}}
        error={Boolean(props.errors.residualDisclosure?.previousRelease)}
        required
        aria-describedby="previousRelease-helperText"
      >
        <FormLabel component="legend">
          {t("requestForm.residualDisclosure.previousRelease")}
          <span className="screen-reader-text">{t("inputs.required")}</span>
        </FormLabel>
        <FormHelperText id="previousRelease-helperText">
          {t(props.errors.residualDisclosure?.previousRelease)}
        </FormHelperText>
        <RadioGroup
          id="previousRelease"
          name="previousRelease"
          onChange={props.readOnly ? null : handleConditionalValueChange}
          value={props.formData.previousRelease}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio color="primary" />}
            label={yesAnswer}
          />
          <FormControlLabel
            value="No"
            control={<Radio color="primary" />}
            label={noAnswer}
          />
        </RadioGroup>
      </FormControl>
      {props.formData.previousRelease==="Yes" && 
        <Box className={classes.indentedSection} sx={{mb: 3}}>
          <FormControl
            component="fieldset"
            sx={{mb: 2}}
            required
            error={Boolean(props.errors.residualDisclosure?.previousRelease_changedSubSample)}
            aria-describedby="previousRelease_changedSubSample-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.residualDisclosure.previousRelease_changedSubSample")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="previousRelease_changedSubSample-helperText">
              {t(props.errors.residualDisclosure?.previousRelease_changedSubSample)}
            </FormHelperText>
            <RadioGroup
              id="previousRelease_changedSubSample"
              name="previousRelease_changedSubSample"
              onChange={props.readOnly ? null : handleValueChange} 
              value={props.formData.previousRelease_changedSubSample}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
          </FormControl>
          <FormControl
            component="fieldset"
            sx={{mb: 2}}
            required
            error={Boolean(props.errors.residualDisclosure?.previousRelease_droppedOutliers)}
            aria-describedby="previousRelease_droppedOutliers-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.residualDisclosure.previousRelease_droppedOutliers")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="previousRelease_droppedOutliers-helperText">
              {t(props.errors.residualDisclosure?.previousRelease_droppedOutliers)}
            </FormHelperText>
            <RadioGroup
              id="previousRelease_droppedOutliers"
              name="previousRelease_droppedOutliers"
              onChange={props.readOnly ? null : handleValueChange} 
              value={props.formData.previousRelease_droppedOutliers}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
          </FormControl>
          <FormControl
            component="fieldset"
            sx={{mb: 2}}
            required
            error={Boolean(props.errors.residualDisclosure?.previousRelease_imputedData)}
            aria-describedby="previousRelease_imputedData-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.residualDisclosure.previousRelease_imputedData")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="previousRelease_imputedData-helperText">
              {t(props.errors.residualDisclosure?.previousRelease_imputedData)}
            </FormHelperText>
            <RadioGroup
              id="previousRelease_imputedData"
              name="previousRelease_imputedData"
              onChange={props.readOnly ? null : handleValueChange} 
              value={props.formData.previousRelease_imputedData}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
          </FormControl>
          <FormControl
            component="fieldset"
            sx={{mb: 2}}
            required
            error={Boolean(props.errors.residualDisclosure?.previousRelease_modifiedVariables)}
            aria-describedby="previousRelease_modifiedVariables-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.residualDisclosure.previousRelease_modifiedVariables")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="previousRelease_modifiedVariables-helperText">
              {t(props.errors.residualDisclosure?.previousRelease_modifiedVariables)}
            </FormHelperText>
            <RadioGroup
              id="previousRelease_modifiedVariables"
              name="previousRelease_modifiedVariables"
              onChange={props.readOnly ? null : handleValueChange} 
              value={props.formData.previousRelease_modifiedVariables}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>
          </FormControl>
          <Box sx={{mb: 3}}>
            <CutCopyPasteTextField
              id="previousRelease_explainChanges"
              name="previousRelease_explainChanges"
              margin="dense"
              label={t("requestForm.residualDisclosure.explainChanges")}
              multiline
              rows="3"
              variant="outlined"
              fullWidth
              required
              onBlur={handleValueChange}
              defaultValue={props.formData.previousRelease_explainChanges}
              error={Boolean(props.errors.residualDisclosure?.previousRelease_explainChanges)}
              helperText={t(props.errors.residualDisclosure?.previousRelease_explainChanges)}
              inputProps={{readOnly: props.readOnly}}
            />
          </Box>
          <Grid component="span" sx={{mb: 3}} container>
            <Grid component="span" xs item>
              <CutCopyPasteTextField
                id="previousRelease_dateRelation"
                name="previousRelease_dateRelation"
                margin="dense"
                label={t("requestForm.residualDisclosure.dateRelation")}
                variant="outlined"
                fullWidth
                required
                onBlur={handleValueChange}
                defaultValue={props.formData.previousRelease_dateRelation}
                error={Boolean(props.errors.residualDisclosure?.previousRelease_dateRelation)}
                helperText={t(props.errors.residualDisclosure?.previousRelease_dateRelation)}
                inputProps={{readOnly: props.readOnly}}
              />
            </Grid>
            <Grid component="span" className={classes.textFieldInfoIcon} item>
              <Tooltip title={t("requestForm.residualDisclosure.dateRelationHelper")} arrow describeChild>
                <IconButton
                  aria-label={t('requestForm.toolTipMoreInformation')}
                  edge="end"
                  size="large">
                  <Icon path={mdiInformationOutline} size={1} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          {supportingFilesSection("previousRelease_residualTablesFile")}
          {supportingFilesSection("previousRelease_bothSetsFile")}
        </Box>
      }
    </Root>
  );
}
ResidualDisclosure.propTypes={
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  filesList: PropTypes.arrayOf(PropTypes.string).isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
};
